import type { Action } from 'vuex'
import type { State } from './state'
import type {
  FileItem,
  QueryDocumentDetailParameters,
  PrepareUploadFiles,
  GetOcrFileDataParameter,
  OCRChapter3Item
} from '@/api/product/model/documentModel'
import type { SortInfo } from '@/views/product/DocumentDetail/types/sort'

import actionTypes from './actionTypes'
import pageMessageActionTypes from '../page-message/actionTypes'
import { queryDocumentDetail, getOCRFileData } from '@/api/product/document'
import { getMessage } from '@/utils/message'
import { deepCopy } from '@/utils'

const actions: { [actionTypes: string]: Action<State, any> } = {
  [actionTypes.SET_MODAL_FILE_ADDITION_ACTIVE_STATUS]: (
    { commit },
    status: boolean
  ): void => commit(actionTypes.SET_MODAL_FILE_ADDITION_ACTIVE_STATUS, status),

  [actionTypes.SET_MODAL_PRODUCT_RELATION_ACTIVE_STATUS]: (
    { commit },
    status: boolean
  ): void => commit(actionTypes.SET_MODAL_PRODUCT_RELATION_ACTIVE_STATUS, status),

  [actionTypes.SET_DOCUMENT_ITEM]: async (
    { commit },
    parameters: QueryDocumentDetailParameters
  ): Promise<void> => {
    const result = await queryDocumentDetail(parameters)
    commit(actionTypes.SET_DOCUMENT_ITEM, result)
    commit(actionTypes.SET_COMPARE_DOCUMENT_ITEM, deepCopy(result))
  },

  [actionTypes.CLEAR_DOCUMENT_ITEM]: ({ commit, dispatch }): void => {
    commit(actionTypes.SET_DOCUMENT_ITEM, {})
    commit(actionTypes.SET_COMPARE_DOCUMENT_ITEM, {})
    dispatch(actionTypes.SET_REMARK_ERROR, '')
  },

  [actionTypes.SET_OCR_DATA_ITEM]: async (
    { commit },
    parameters: GetOcrFileDataParameter
  ): Promise<void> => {
    const result = await getOCRFileData(parameters)

    const emptyItem: OCRChapter3Item = {
      rowNo: 0,
      ingredientName: '',
      casNo: '',
      contentLowerLimit: '',
      contentUpperLimit: '',
      contentFullText: '',
      encsNo: '',
      ishlNo: ''
    };
  
    if (result) {
      for (let i = 1; i <= 20; i++) {
        if (!result.detail.some(item => item.rowNo === i)){
          result.detail.push({ ...emptyItem, rowNo: i });
        }
      }
      result.detail.sort((a:OCRChapter3Item, b:OCRChapter3Item) => a.rowNo - b.rowNo);
    }
  
    commit(actionTypes.SET_OCR_DATA_ITEM, result)
    commit(actionTypes.SET_COMPARE_OCR_DATA_ITEM, deepCopy(result))
  },

  [actionTypes.REVERT_OCR_DATA_ITEM]: ({ commit, state }): void => {
    commit(actionTypes.SET_OCR_DATA_ITEM, deepCopy(state.compareOcrDataItem))
  },

  [actionTypes.SET_REMARK]: ({ commit, dispatch }, remark: string): void => {
    commit(actionTypes.SET_REMARK, remark)
    dispatch(actionTypes.SET_REMARK_ERROR, '')

    dispatch(
      pageMessageActionTypes.DELETE_ONE_PAGE_MESSAGE,
      getMessage('EF0033', ['備考', '入力'])
    )

    dispatch(actionTypes.SET_FORM_UPDATE_STATUS, true)
  },

  [actionTypes.REVERT_REMARK]: ({ dispatch, state }): void => {
    dispatch(actionTypes.SET_REMARK, state.compareDocumentItem.remark)
  },

  [actionTypes.SET_REMARK_ERROR]: ({ commit }, error: string): void => {
    commit(actionTypes.SET_REMARK_ERROR, error)
  },

  [actionTypes.SET_FILE_LIST]: ({ commit, dispatch }, fileList) => {
    commit(actionTypes.SET_FILE_LIST, fileList)
    dispatch(actionTypes.SET_NEW_FILE_REVISION_DATE, '')
  },

  [actionTypes.REVERT_FILE_LIST]: ({ commit, state }): void => {
    commit(actionTypes.SET_FILE_LIST, deepCopy(state.compareDocumentItem.file_list))
  },

  [actionTypes.DELETE_FILE]: ({ commit, dispatch, state }, fileID: number): void => {
    const list = state.originalDocumentItem.file_list.filter(
      (item: FileItem) => item.file_id !== fileID
    )

    commit(actionTypes.SET_FILE_LIST, list)
    dispatch(actionTypes.SET_FORM_UPDATE_STATUS, true)
  },

  [actionTypes.SET_SELECTED_FILE]: ({ commit }, file: File | undefined): void => {
    commit(actionTypes.SET_SELECTED_FILE, file)
  },

  [actionTypes.SET_SELECTED_FILE_ERROR]: ({ commit }, error: string): void => {
    commit(actionTypes.SET_SELECTED_FILE_ERROR, error)
  },

  [actionTypes.SET_SELECT_FILE_UNDER_ERROR]: ({ commit }, error: string): void => {
    commit(actionTypes.SET_SELECT_FILE_UNDER_ERROR, error)
  },

  [actionTypes.SET_NEW_FILE_REVISION_DATE]: (
    { commit, dispatch },
    date: string
  ): void => {
    date && dispatch(actionTypes.SET_NEW_FILE_REVISION_DATE_ERROR, '')
    commit(actionTypes.SET_NEW_FILE_REVISION_DATE, date)
  },

  [actionTypes.SET_NEW_FILE_REVISION_DATE_ERROR]: ({ commit }, error: string): void => {
    commit(actionTypes.SET_NEW_FILE_REVISION_DATE_ERROR, error)
  },

  [actionTypes.SET_PREPARE_UPLOAD_FILES]: (
    { commit },
    fileList: PrepareUploadFiles[]
  ): void => {
    commit(actionTypes.SET_PREPARE_UPLOAD_FILES, fileList)
  },

  [actionTypes.SET_FORM_UPDATE_STATUS]({ commit }, status: boolean): void {
    commit(actionTypes.SET_FORM_UPDATE_STATUS, status)
  },

  //ドキュメント詳細画面　ファイル一覧ソート情報
  [actionTypes.SET_SORT_INFO]({ commit }, info: SortInfo): void {
    commit(actionTypes.SET_SORT_INFO, info)
  },

  [actionTypes.SET_UPLOAD_FILE_TYPE]({ commit }, val: string): void {
    commit(actionTypes.SET_UPLOAD_FILE_TYPE, val)
  },

  [actionTypes.SET_OCR_UPDATE_MESSAGE]: ({ commit }, message: string): void => {
    commit(actionTypes.SET_OCR_UPDATE_MESSAGE, message)
  },
}

export default actions
